import jooiceAiApi from '@api/jooice-ai/jooice-ai';
import policyAPI from '@api/policy/policy';
import { ICreditBalance } from '@models/jooice-ai/response';
import { EPolicyDomain, EPolicyNames, IFindOneMembershipPackagesResponse } from '@models/policy/response';
import { useQuery } from '@tanstack/react-query';
import queryKeys from '@utils/queryKeys';
import { createContext, ReactNode, useContext, useMemo } from 'react';
import { useClerkMe } from 'src/contexts/ClerkMeContext';
import { isClerkAuthEnabled } from 'src/utils/clerkAuth';

interface IPolicyObject {
  listing: number;
  reputation: number | null;
  jooiceAi: ICreditBalance;
}

export interface IPolicyContext {
  limits: IPolicyObject;
  policy: IFindOneMembershipPackagesResponse | undefined;
  refetchPolicy: VoidFunction;
  refetchAiCredits: VoidFunction;
  hasListing: boolean | undefined;
  hasListingPro: boolean;
  hasReputation: boolean | undefined;
  hasReputationPro: boolean;
  hasSocial: boolean | undefined;
  hasSocialPro: boolean;
  hasJooiceAi: boolean | undefined;
  hasJooiceAiText: boolean;
  hasJooiceAiImages: boolean;
  canTextJooiceAi: boolean;
  canImageJooiceAi: boolean;
  subscriptionPlan: string;
  plan: EPolicyDomain | null;
  loading: boolean;
}

const PolicyContext = createContext<IPolicyContext>({
  limits: {
    listing: 0,
    reputation: 0,
    jooiceAi: { operations: [] },
  },
  policy: undefined,
  refetchPolicy: () => {},
  refetchAiCredits: () => {},
  hasListing: undefined,
  hasListingPro: false,
  hasReputation: undefined,
  hasReputationPro: false,
  hasSocial: undefined,
  hasSocialPro: false,
  hasJooiceAi: undefined,
  hasJooiceAiText: false,
  hasJooiceAiImages: false,
  canTextJooiceAi: false,
  canImageJooiceAi: false,
  subscriptionPlan: '',
  plan: null,
  loading: false,
});

export const PolicyContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const isClerkMode = isClerkAuthEnabled();
  const { data: clerkMe, isFetching: isClerkMeFetching, refetch: refetchClerkMe } = useClerkMe();
  const {
    data: policyData,
    refetch: refetchPolicy,
    isFetching: isPolicyFetching,
  } = useQuery({
    queryKey: ['getPolicyPlanInfo'],
    queryFn: () => policyAPI.getPolicyPlanInfo(),
    refetchOnWindowFocus: false,
    enabled: !isClerkMode,
  });
  const {
    data: usedAiCreditsData,
    refetch: refetchAiCredits,
    isFetching: isAiCreditsFetching,
  } = useQuery({
    queryKey: queryKeys.jooiceAi.remainingAiCredits,
    queryFn: jooiceAiApi.aiRemainingCredits,
    refetchOnWindowFocus: false,
  });

  // jooice-ai GET /v1/credits answers in the caller's mode: legacy text/image operations for Outseta,
  // one `aiCredits` operation (Policy ledger balance) for Clerk.
  const aiCredits = usedAiCreditsData ?? { operations: [] };

  const clerkEntitlements = clerkMe?.entitlements;
  const clerkFeatures = clerkEntitlements?.features ?? [];
  const hasListing = isClerkMode
    ? clerkFeatures.some((x: { domain: string }) => x.domain === EPolicyDomain.LISTINGS)
    : !!policyData?.features.find((x) => x.domain === EPolicyDomain.LISTINGS);
  const hasReputation = isClerkMode
    ? clerkFeatures.some((x: { domain: string }) => x.domain === EPolicyDomain.INTERACTIONS)
    : !!policyData?.features.find((x) => x.domain === EPolicyDomain.INTERACTIONS);
  const hasSocial = isClerkMode
    ? clerkFeatures.some((x: { domain: string }) => x.domain === EPolicyDomain.SOCIAL)
    : !!policyData?.features.find((x) => x.domain === EPolicyDomain.SOCIAL);

  const limits = useMemo(() => {
    if (isClerkMode) {
      return {
        description: clerkMe?.subscription?.planKey ?? '',
        listing: clerkEntitlements?.listingLocationQuantity ?? 0,
        reputation: hasReputation ? null : 0,
        jooiceAi: aiCredits,
      };
    }
    if (!policyData) return;
    return {
      description: policyData.name,
      listing: policyData.quantity ?? 0,
      reputation: policyData.features.find((x) => x.domain === EPolicyDomain.INTERACTIONS) ? null : 0,
      jooiceAi: aiCredits,
    };
  }, [isClerkMode, clerkMe, clerkEntitlements, hasReputation, aiCredits, policyData]);

  const loading = (isClerkMode ? isClerkMeFetching : isPolicyFetching) || isAiCreditsFetching;

  const getPolicyDomain = (): EPolicyDomain => {
    if (hasListing && hasReputation && hasSocial) {
      return EPolicyDomain.BUNDLE;
    }
    if (hasListing && !hasReputation) {
      return EPolicyDomain.LISTINGS;
    }
    if (!hasListing && hasReputation) {
      return EPolicyDomain.INTERACTIONS;
    }
    if (!hasListing && !hasReputation) {
      return EPolicyDomain.SOCIAL;
    }
    return EPolicyDomain.LISTINGS;
  };

  const plan = useMemo(() => getPolicyDomain(), [hasListing, hasReputation, hasSocial]);

  if (!limits) return <>{children}</>;

  return (
    <PolicyContext.Provider
      value={{
        limits,
        policy: policyData,
        plan,
        refetchPolicy: isClerkMode ? refetchClerkMe : refetchPolicy,
        refetchAiCredits,
        hasListing,
        hasListingPro: isClerkMode
          ? clerkFeatures.some(
              (x) =>
                x.domain === EPolicyDomain.LISTINGS &&
                [EPolicyNames.LISTING_PRO, EPolicyNames.LISTING_ESSENTIAL, EPolicyNames.LISTING_MAX].includes(
                  x.name as EPolicyNames,
                ),
            )
          : !!policyData?.features.find(
              (x) =>
                x.domain === EPolicyDomain.LISTINGS &&
                [EPolicyNames.LISTING_PRO, EPolicyNames.LISTING_ESSENTIAL, EPolicyNames.LISTING_MAX].includes(
                  x.name as EPolicyNames,
                ),
            ),
        hasReputation,
        hasReputationPro: isClerkMode
          ? clerkFeatures.some((x) => x.domain === EPolicyDomain.INTERACTIONS && x.name === EPolicyNames.REPUTATION_PRO)
          : !!policyData?.features.find(
              (x) => x.domain === EPolicyDomain.INTERACTIONS && x.name === EPolicyNames.REPUTATION_PRO,
            ),
        hasSocial,
        hasSocialPro: isClerkMode
          ? clerkFeatures.some(
              (x) =>
                x.domain === EPolicyDomain.SOCIAL &&
                [EPolicyNames.SOCIAL_PRO, EPolicyNames.SOCIAL_NETWORKS].includes(x.name as EPolicyNames),
            )
          : !!policyData?.features.find(
              (x) => x.domain === EPolicyDomain.INTERACTIONS && x.name === EPolicyNames.SOCIAL_PRO,
            ),
        hasJooiceAi: isClerkMode
          ? clerkFeatures.some((x) => x.domain === EPolicyDomain.JOOICE_AI)
          : !!policyData?.features.find((x) => x.domain === EPolicyDomain.JOOICE_AI),
        hasJooiceAiText: (() => {
          const op = limits.jooiceAi?.operations?.find((o) => o.operation === 'textGeneration');
          return op !== undefined && (op.limit ?? 0) > 0;
        })(),
        hasJooiceAiImages: (() => {
          const op = limits.jooiceAi?.operations?.find((o) => o.operation === 'imageGeneration');
          return op !== undefined && (op.limit ?? 0) > 0;
        })(),
        canTextJooiceAi: (() => {
          const op = limits.jooiceAi?.operations?.find((o) => o.operation === 'textGeneration');
          return op !== undefined && op.remaining > 0;
        })(),
        canImageJooiceAi: (() => {
          const op = limits.jooiceAi?.operations?.find((o) => o.operation === 'imageGeneration');
          return op !== undefined && op.remaining > 0;
        })(),
        subscriptionPlan: isClerkMode ? (clerkMe?.subscription?.planKey ?? '') : policyData?.name || '',
        loading,
      }}
    >
      {children}
    </PolicyContext.Provider>
  );
};

export const usePolicy = () => {
  const context = useContext(PolicyContext);
  if (context === undefined) {
    throw new Error('Policy context must be called inside a provider');
  }
  return context;
};
